import { useState, useEffect } from 'react';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'What is Vollet?',
      answer: 'Vollet is a non-custodial cryptocurrency wallet that allows you to send, receive, and manage your digital assets securely. You maintain full control of your private keys and crypto at all times.'
    },
    {
      question: 'Is Vollet non-custodial?',
      answer: 'Yes, Vollet is 100% non-custodial. This means you own and control your private keys. Vollet never has access to your funds or private keys - they are stored securely on your device only.'
    },
    {
      question: 'What if I lose my phone?',
      answer: 'If you lose your phone, you can recover your wallet using your backup recovery phrase. This is why it\'s critical to write down and securely store your recovery phrase when you first create your wallet. Never share it with anyone.'
    },
    {
      question: 'Does Vollet charge fees?',
      answer: 'Vollet itself does not charge any fees for using the wallet. However, you will need to pay standard blockchain network fees (gas fees) when sending transactions. These fees go to the network validators, not to Vollet.'
    },
    {
      question: 'Which cryptocurrencies are supported?',
      answer: 'Vollet currently supports Bitcoin (BTC), Ethereum (ETH), and popular ERC-20 tokens. We are continuously adding support for more cryptocurrencies and blockchain networks.'
    },
    {
      question: 'How do I back up my wallet?',
      answer: 'When you create a new wallet, Vollet will provide you with a 12 or 24-word recovery phrase. Write this down on paper and store it in a safe place. This phrase is the only way to recover your wallet if you lose access to your device. Never store it digitally or share it with anyone.'
    }
  ];

  useEffect(() => {
    // Add FAQ Schema.org JSON-LD
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqSchema);
    script.id = 'schema-faq';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('schema-faq');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Frequently asked questions
          </h2>
          <p className="text-lg text-gray-600">
            Got questions? We've got answers.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`ri-arrow-down-s-line text-xl text-gray-500 transition-transform ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>
              {openIndex === index && (
                <div className="px-5 pb-5">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
